/**
 * LeadsFilters.tsx — the compact filter + search bar for the Leads list.
 *
 * The panel leads with the DATA, not the form: applied filters show as a row of
 * removable chips, and the full form lives inside a collapsed "Mais filtros"
 * <details> disclosure (default closed) so it never fills the viewport and
 * pushes the table below the fold. Opening the disclosure reveals the same
 * status multi-select, metadata fields, date range and search controls as before.
 *
 * It owns its own form state and, on submit, resolves a ready-to-send
 * `LeadsQuery`. The single search box (name, CPF or phone; ADR-089) applies
 * itself 300 ms after the operator stops typing; its text is personal data and
 * is held only in this component's state and the POST body. Chips are derived
 * from the last APPLIED query (passed in), so removing a chip re-applies a
 * narrowed query directly, without reopening the form.
 *
 * Accessibility: every control has an associated <label>; the status group is a
 * <fieldset>/<legend>; the disclosure is a native <details>/<summary>; each chip
 * remove is a real button with a descriptive aria-label. Apply/Reset are real
 * buttons in a <form> so the panel is fully keyboard-operable (WCAG 2.1.1,
 * 1.3.1, 4.1.2).
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadsQuery } from '../../api/client';
import { formatDateOnly } from './formatDateTime';
import { StatusMultiSelect } from './StatusMultiSelect';
import { LEADS_PAGE_SIZE } from './useLeadsQuery';
import { FilterChips, type FilterChip } from '../../components/FilterChips';
import { Field, inputClass } from '../../components/Field';

export interface LeadsFiltersProps {
  /** Called with a resolved, hash-ready query when the operator applies filters. */
  onApply: (query: LeadsQuery) => void;
  /** Whether a fetch is in flight (disables Apply to avoid overlap). */
  busy: boolean;
  /** The last APPLIED query — drives the removable chip row. */
  applied: LeadsQuery;
  /** Active locale for date-chip formatting (pt-BR → dd/mm/yyyy). */
  locale: string;
  /** The server's rejection of the last search term (fixed message), shown inline. */
  searchError?: string | null;
}

/** Search debounce (ADR-089 / design F2). */
export const SEARCH_DEBOUNCE_MS = 300;

// Same rule as capital-service: number-like input needs 4+ digits, text needs 2-100 chars.
const NUMBER_LIKE = /^[\d\s.\-()+/]+$/;
export const searchProblem = (term: string): string | null => {
  if (term === '') return null;
  if (term.length > 100) return 'leads.search.lengthMax';
  if (NUMBER_LIKE.test(term))
    return term.replace(/\D/g, '').length < 4 ? 'leads.search.digitsMin' : null;
  return term.length < 2 ? 'leads.search.lengthMin' : null;
};

interface FormState {
  productCode: string;
  channel: string;
  tenantId: string;
  createdFrom: string;
  createdTo: string;
  statusFrom: string;
  statusTo: string;
}

const emptyForm = (): FormState => ({
  productCode: '',
  channel: '',
  tenantId: '',
  createdFrom: '',
  createdTo: '',
  statusFrom: '',
  statusTo: '',
});

// A form draft seeded from an applied query (the search text lives in its own state).
const queryToForm = (q: LeadsQuery): FormState => ({
  productCode: q.product_code ?? '',
  channel: q.channel ?? '',
  tenantId: q.tenant_id ?? '',
  createdFrom: q.created_from ?? '',
  createdTo: q.created_to ?? '',
  statusFrom: q.status_from ?? '',
  statusTo: q.status_to ?? '',
});

const blank = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export const LeadsFilters = ({
  onApply,
  busy,
  applied,
  locale,
  searchError = null,
}: LeadsFiltersProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => queryToForm(applied));
  const [searchText, setSearchText] = useState(applied.q ?? '');
  const term = searchText.trim();
  const problemKey = searchProblem(term);
  // Latest applied query, read inside the debounce timer without re-arming it.
  const appliedRef = useRef(applied);
  appliedRef.current = applied;

  // Client-side rule first; else the server's fixed 422 text (generic copy if it sent none).
  let searchMessage: string | undefined;
  if (problemKey != null) searchMessage = t(problemKey);
  else if (searchError != null) searchMessage = searchError || t('leads.search.rejected');

  // Debounced search: apply 300 ms after the last keystroke; a blocked term is never sent.
  useEffect(() => {
    if (problemKey != null) return undefined;
    const id = setTimeout(() => {
      const { current } = appliedRef;
      if (term === (current.q ?? '')) return;
      onApply({ ...current, q: term === '' ? null : term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term, problemKey, onApply]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (problemKey != null) return;
      onApply({
        q: term === '' ? null : term,
        status: applied.status ?? [],
        product_code: blank(form.productCode),
        channel: blank(form.channel),
        tenant_id: blank(form.tenantId),
        created_from: blank(form.createdFrom),
        created_to: blank(form.createdTo),
        status_from: blank(form.statusFrom),
        status_to: blank(form.statusTo),
        limit: LEADS_PAGE_SIZE,
      });
    },
    [form, term, problemKey, applied.status, onApply],
  );

  const handleReset = useCallback(() => {
    setForm(emptyForm());
    setSearchText('');
    onApply({ limit: LEADS_PAGE_SIZE });
  }, [onApply]);

  // Re-apply the applied query minus one field, and keep the draft in sync.
  const applyNarrowed = useCallback(
    (next: LeadsQuery) => {
      onApply(next);
      setForm(queryToForm(next));
    },
    [onApply],
  );

  // --- Applied-filter chips (derived from the applied query) ---------------
  const chips: FilterChip[] = [];
  const removeChipLabel = (label: string): string => t('filters.remove', { label });
  const textChip = (field: 'product_code' | 'channel' | 'tenant_id', labelKey: string): void => {
    const value = applied[field];
    if (value == null || value === '') return;
    const label = `${t(labelKey)}: ${value}`;
    chips.push({
      key: field,
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => applyNarrowed({ ...applied, [field]: null }),
    });
  };
  textChip('product_code', 'leads.filters.productCode');
  textChip('channel', 'leads.filters.channel');
  textChip('tenant_id', 'leads.filters.tenant');
  const dateChip = (
    field: 'created_from' | 'created_to' | 'status_from' | 'status_to',
    labelKey: string,
  ): void => {
    const value = applied[field];
    if (value == null || value === '') return;
    const label = `${t(labelKey)}: ${formatDateOnly(value, locale)}`;
    chips.push({
      key: field,
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => applyNarrowed({ ...applied, [field]: null }),
    });
  };
  dateChip('created_from', 'leads.filters.createdFrom');
  dateChip('created_to', 'leads.filters.createdTo');
  dateChip('status_from', 'leads.filters.statusFrom');
  dateChip('status_to', 'leads.filters.statusTo');

  return (
    <form
      aria-label={t('leads.filters.legend')}
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-xl bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/10"
    >
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-meta uppercase tracking-wide text-ink-3">
            {t('filters.active')}
          </span>
          <FilterChips chips={chips} testid="leads-filter-chips" />
        </div>
      ) : null}

      {/* Search + status stay visible: the two most-used controls (spec slice 4). */}
      <Field
        id="search-value"
        label={t('leads.search.legend')}
        hint={t('leads.search.hint')}
        error={searchMessage}
      >
        <input
          id="search-value"
          type="search"
          autoComplete="off"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className={`${inputClass} sm:max-w-md max-md:min-h-11`}
        />
      </Field>

      {/* Status multi-select — empty = active scope (Q2); applies immediately, resets to page 1. */}
      <StatusMultiSelect
        value={applied.status ?? []}
        onChange={(status) => onApply({ ...applied, status })}
      />

      <details className="flex flex-col gap-4" data-testid="leads-filters-disclosure">
        <summary className="cursor-pointer select-none text-body font-medium text-ink-1 marker:text-ink-3">
          {t('filters.more')}
        </summary>

        <div className="mt-3 flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Field id="filter-product" label={t('leads.filters.productCode')}>
              <input
                id="filter-product"
                type="text"
                value={form.productCode}
                onChange={(e) => setForm((p) => ({ ...p, productCode: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-channel" label={t('leads.filters.channel')}>
              <input
                id="filter-channel"
                type="text"
                value={form.channel}
                onChange={(e) => setForm((p) => ({ ...p, channel: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-tenant" label={t('leads.filters.tenant')}>
              <input
                id="filter-tenant"
                type="text"
                value={form.tenantId}
                placeholder={t('leads.filters.tenantAll')}
                onChange={(e) => setForm((p) => ({ ...p, tenantId: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-created-from" label={t('leads.filters.createdFrom')}>
              <input
                id="filter-created-from"
                type="date"
                lang={locale}
                value={form.createdFrom}
                onChange={(e) => setForm((p) => ({ ...p, createdFrom: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-created-to" label={t('leads.filters.createdTo')}>
              <input
                id="filter-created-to"
                type="date"
                lang={locale}
                value={form.createdTo}
                onChange={(e) => setForm((p) => ({ ...p, createdTo: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-status-from" label={t('leads.filters.statusFrom')}>
              <input
                id="filter-status-from"
                type="date"
                lang={locale}
                value={form.statusFrom}
                onChange={(e) => setForm((p) => ({ ...p, statusFrom: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="filter-status-to" label={t('leads.filters.statusTo')}>
              <input
                id="filter-status-to"
                type="date"
                lang={locale}
                value={form.statusTo}
                onChange={(e) => setForm((p) => ({ ...p, statusTo: e.target.value }))}
                className={inputClass}
              />
            </Field>
          </div>
        </div>
      </details>

      <div className="flex gap-3">
        <button
          type="submit"
          data-testid="leads-apply"
          disabled={busy}
          aria-busy={busy}
          className="inline-flex items-center rounded-lg bg-gold min-h-11 px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold-mute disabled:opacity-60"
        >
          {t('leads.filters.apply')}
        </button>
        <button
          type="button"
          data-testid="leads-reset"
          onClick={handleReset}
          disabled={busy}
          className="inline-flex items-center rounded-lg bg-surface-1 min-h-11 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
        >
          {t('leads.filters.reset')}
        </button>
      </div>
    </form>
  );
};
